import { useState } from "react";
import { loadProgress, recordShift, saveProgress, type Progress, type Recorded } from "./game/progress";
import type { Restaurant } from "./game/restaurants";
import { loadSettings, saveSettings, type Settings } from "./game/settings";
import type { ShiftResult } from "./game/shift";
import { RestaurantList } from "./ui/RestaurantList";
import { Results } from "./ui/Results";
import { ShiftScreen } from "./ui/ShiftScreen";

type Screen =
  | { kind: "board" }
  | { kind: "shift"; restaurant: Restaurant; seed: number }
  | { kind: "results"; restaurant: Restaurant; result: ShiftResult; recorded: Recorded };

function newSeed(): number {
  return Math.floor(Math.random() * 2 ** 31);
}

export function App() {
  const [progress, setProgress] = useState<Progress>(loadProgress);
  const [screen, setScreen] = useState<Screen>({ kind: "board" });
  const [settings, setSettings] = useState<Settings>(loadSettings);
  const changeSettings = (s: Settings) => {
    setSettings(s);
    saveSettings(s);
  };

  const startShift = (restaurant: Restaurant) => setScreen({ kind: "shift", restaurant, seed: newSeed() });

  const finish = (restaurant: Restaurant, result: ShiftResult) => {
    const recorded = recordShift(progress, restaurant.id, result);
    setProgress(recorded.progress);
    saveProgress(recorded.progress);
    setScreen({ kind: "results", restaurant, result, recorded });
  };

  switch (screen.kind) {
    case "board":
      return <RestaurantList progress={progress} onPick={startShift} settings={settings} onSettings={changeSettings} />;
    case "shift":
      return (
        <ShiftScreen
          key={screen.seed}
          restaurant={screen.restaurant}
          seed={screen.seed}
          settings={settings}
          onSettings={changeSettings}
          onFinish={(result) => finish(screen.restaurant, result)}
          onQuit={() => setScreen({ kind: "board" })}
        />
      );
    case "results":
      return (
        <Results
          restaurant={screen.restaurant}
          result={screen.result}
          recorded={screen.recorded}
          onAgain={() => startShift(screen.restaurant)}
          onBoard={() => setScreen({ kind: "board" })}
        />
      );
  }
}
